import { ref, computed, resolveComponent, openBlock, createBlock, withCtx, createElementVNode, normalizeClass, withModifiers, renderSlot, createElementBlock, Fragment, renderList, createCommentVNode, toDisplayString, normalizeStyle, watch, onMounted, onBeforeUnmount, onUnmounted, createApp, h } from 'vue';
import { createComponent } from './utils/create-component';
import { throttle } from './utils/base';
import { SHOW_EVENT, HIDE_EVENT, SELECT_EVENT, INPUT_EVENT } from './utils/constants';
import { bindOurFnToEventOfNode } from './utils/vnode';
import { getFirstScrollWrapper, getRelativePosition, getFirstInvisibleWrapper } from './utils/position';
import { getArrowPadding, findBestBubblePosition, computeBubblePosition } from './utils/bubble';
import mitt from 'mitt';
import propTypes from './utils/prop-types';
import Transition from './transition';
import Icon from './icon';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

// 位置定义
var popoverPositions = ['top', 'bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end'];
var defaultPosition = popoverPositions[0];

var props = {
  /**
   * 弹出/隐藏
   */
  modelValue: propTypes["boolean"]().def(false),
  /**
   * 位置
   * 可选值：top/bottom/top-start/top-end/bottom-start/bottom-end
   */
  position: propTypes.string().def(defaultPosition),
  /**
   * 菜单列表
   */
  menu: propTypes.array(),
  /**
   * 停留时间
   */
  duration: propTypes.number().def(0),
  /**
   * 点击任意位置能隐藏popover
   */
  touchHide: propTypes["boolean"]().def(true),
  /**
   * 点击宿主元素能展示popover
   */
  touchShow: propTypes["boolean"]().def(true),
  /**
   * 是否启用位置自适应
   */
  isAdaptive: propTypes["boolean"]().def(false),
  /**
   * 箭头水平最小偏移量（小于这个偏移量，气泡框将不会对齐宿主元素边缘；单位为px）
   */
  arrowHorizontalThreshold: propTypes.number().def(16),
  /**
   * 箭头垂直最小偏移量（小于这个偏移量，气泡框将不会对齐宿主元素边缘；单位为px）
   */
  arrowVerticalThreshold: propTypes.number().def(8),
  /**
   * 箭头尖端与宿主元素边缘的位置偏移量
   */
  bubbleJustifyOffset: propTypes.number().def(8)
};
var bubbleProps = {
  /**
   * 位置
   * 可选值：top/bottom/top-start/top-end/bottom-start/bottom-end
   */
  position: propTypes.object().def(ref(defaultPosition)),
  /**
   * 菜单列表
   */
  menu: propTypes.object().def(ref([])),
  /**
   * 宿主半宽，渲染之后将会传入
   */
  halfSlotWidth: propTypes.object().def(ref(0)),
  /**
   * 宿主半高，渲染之后将会传入
   */
  halfSlotHeight: propTypes.object().def(ref(0)),
  /**
   * 气泡半宽，渲染之后将会传入
   */
  halfBubbleWidth: propTypes.object().def(ref(0)),
  /**
   * 气泡半高，渲染之后将会传入
   */
  halfBubbleHeight: propTypes.object().def(ref(0)),
  /**
   * 箭头水平最小偏移量（小于这个偏移量，气泡框将不会对齐宿主元素边缘；单位为px）
   */
  arrowHorizontalThreshold: propTypes.object().def(ref(16)),
  /**
   * 箭头垂直最小偏移量（小于这个偏移量，气泡框将不会对齐宿主元素边缘；单位为px）
   */
  arrowVerticalThreshold: propTypes.object().def(ref(8))
};
var emits = [SHOW_EVENT, HIDE_EVENT, SELECT_EVENT, INPUT_EVENT];

var _components;
var emitter = mitt();
var _createComponent$1 = createComponent('popover'),
  create$1 = _createComponent$1.create,
  className = _createComponent$1.className;
var script = create$1({
  name: 'popover-bubble',
  components: (_components = {}, _defineProperty(_components, Transition.name, Transition), _defineProperty(_components, Icon.name, Icon), _components),
  emits: emits,
  props: bubbleProps,
  setup: function setup(props, _ref) {
    _ref.emit;
      var slots = _ref.slots;
    var popClasses = computed(function () {
      var _ref2;
      return _ref2 = {}, _defineProperty(_ref2, "".concat(className), true), _defineProperty(_ref2, "".concat(className, "-").concat(props.position.value), true), _ref2;
    });
    var menuClasses = computed(function () {
      return _defineProperty({}, "".concat(className, "__menu"), true);
    });
    var menuItemClass = "".concat(className, "__menu-item");
    var menuItemTextClass = "".concat(className, "__menu-item_text");
    var arrowClass = "".concat(className, "__arrow");
    // 将计算出的箭头位置以css变量的形式传给css解析器
    var arrowHorizontalPadding = computed(function () {
      return getArrowPadding({
        threshold: props.arrowHorizontalThreshold.value,
        slotHalf: props.halfSlotWidth.value,
        bubbleHalf: props.halfBubbleWidth.value,
        mode: 'horizontal'
      });
    });
    var arrowVerticalPadding = computed(function () {
      return getArrowPadding({
        threshold: props.arrowVerticalThreshold.value,
        slotHalf: props.halfSlotHeight.value,
        bubbleHalf: props.halfBubbleHeight.value,
        mode: 'vertical'
      });
    });
    var arrowStyle = computed(function () {
      return {
        '--sp-popover-size-container-arrow-horizontal-padding': "".concat(arrowHorizontalPadding.value, "px"),
        '--sp-popover-size-container-arrow-vertical-padding': "".concat(arrowVerticalPadding.value, "px")
      };
    });
    var menuItemIconClass = "".concat(className, "__menu-item_icon");
    var handleItemClick = function handleItemClick(item) {
      emitter.emit(SELECT_EVENT, item);
    };
    return {
      popClasses: popClasses,
      menuClasses: menuClasses,
      menuItemClass: menuItemClass,
      menuItemTextClass: menuItemTextClass,
      arrowClass: arrowClass,
      arrowStyle: arrowStyle,
      menuItemIconClass: menuItemIconClass,
      handleItemClick: handleItemClick,
      slots: slots
    };
  }
});

var _hoisted_1 = ["onClick"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_icon = resolveComponent("sp-icon");
  var _component_sp_transition = resolveComponent("sp-transition");
  return openBlock(), createBlock(_component_sp_transition, {
    name: "popover-transition",
    appear: ""
  }, {
    "default": withCtx(function () {
      return [createElementVNode("div", {
        "class": normalizeClass(_ctx.popClasses),
        onTouchstart: _cache[0] || (_cache[0] = withModifiers(function () {}, ["stop"])),
        onTouchmove: _cache[1] || (_cache[1] = withModifiers(function () {}, ["stop"])),
        onTouchend: _cache[2] || (_cache[2] = withModifiers(function () {}, ["stop"])),
        onClick: _cache[3] || (_cache[3] = withModifiers(function () {}, ["stop"]))
      }, [_ctx.slots["default"] ? renderSlot(_ctx.$slots, "default", {
        key: 0
      }) : (openBlock(), createElementBlock("div", {
        key: 1,
        "class": normalizeClass(_ctx.menuClasses)
      }, [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.menu.value, function (item) {
        return openBlock(), createElementBlock("div", {
          "class": normalizeClass(_ctx.menuItemClass),
          key: item.value,
          onClick: withModifiers(function ($event) {
            return _ctx.handleItemClick(item);
          }, ["stop"])
        }, [item.icon ? (openBlock(), createBlock(_component_sp_icon, {
          key: 0,
          "class": normalizeClass(_ctx.menuItemIconClass),
          name: item.icon,
          color: "#222222",
          size: "22px"
        }, null, 8 /* PROPS */, ["class", "name"])) : createCommentVNode("v-if", true), createElementVNode("span", {
          "class": normalizeClass(_ctx.menuItemTextClass)
        }, toDisplayString(item.text), 3 /* TEXT, CLASS */)], 10 /* CLASS, PROPS */, _hoisted_1);
      }), 128 /* KEYED_FRAGMENT */))], 2 /* CLASS */)), createElementVNode("div", {
        "class": normalizeClass(_ctx.arrowClass),
        style: normalizeStyle(_objectSpread2({}, _ctx.arrowStyle))
      }, null, 6 /* CLASS, STYLE */)], 34 /* CLASS, HYDRATE_EVENTS */)];
    }),

    _: 3 /* FORWARDED */
  });
}

script.render = render;
script.__file = "components/src/component/popover/bubble.vue";

var _createComponent = createComponent('popover'),
  create = _createComponent.create;
var Popover = create({
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
      emit = _ref.emit;
    var bubbleVM;
    var timer;
    var bubbleContainer; // 放置bubble的container
    var slotContainer; // 放置slot的container
    var bubbleVMJustCreated;
    var firstSlotNode;
    var position = ref(props.position);
    var menu = computed(function () {
      return props.menu;
    });
    var halfSlotWidth = ref(0);
    var halfSlotHeight = ref(0);
    var halfBubbleWidth = ref(16);
    var halfBubbleHeight = ref(8);
    var arrowHorizontalThreshold = computed(function () {
      return props.arrowHorizontalThreshold < halfBubbleWidth.value ? props.arrowHorizontalThreshold : halfBubbleWidth.value;
    });
    var arrowVerticalThreshold = computed(function () {
      return props.arrowVerticalThreshold < halfBubbleHeight.value ? props.arrowVerticalThreshold : halfBubbleHeight.value;
    });
    function showBubble() {
      if (!bubbleContainer) {
        return;
      }
      createBubble();
      if (bubbleVM && bubbleVM.$el.parentNode !== bubbleContainer) {
        bubbleContainer.appendChild(bubbleVM.$el);
      }
      bubbleContainer.addEventListener('scroll', handleScroll);
      placeBubble();
      if (bubbleVMJustCreated) {
        emit(INPUT_EVENT, true);
        emit(SHOW_EVENT);
      }
      if (props.duration && props.duration > 0) {
        timer && clearTimeout(timer);
        timer = setTimeout(function () {
          hideBubble();
        }, props.duration);
      }
    }
    function createBubble() {
      if (!bubbleVM) {
        var slotElRect = slotContainer.getBoundingClientRect();
        halfSlotWidth.value = slotElRect.width / 2;
        halfSlotHeight.value = slotElRect.height / 2;
        if (slots.content) {
          var contentSlot = slots.content();
          bubbleVM = createApp({
            render: function render() {
              return h(script, {
                position: position,
                halfSlotWidth: halfSlotWidth,
                halfSlotHeight: halfSlotHeight,
                halfBubbleWidth: halfBubbleWidth,
                halfBubbleHeight: halfBubbleHeight,
                arrowHorizontalThreshold: arrowHorizontalThreshold,
                arrowVerticalThreshold: arrowVerticalThreshold
              }, function () {
                return contentSlot;
              });
            }
          }).mount(document.createElement('div'));
        } else {
          bubbleVM = createApp(script, {
            position: position,
            menu: menu,
            halfSlotWidth: halfSlotWidth,
            halfSlotHeight: halfSlotHeight,
            halfBubbleWidth: halfBubbleWidth,
            halfBubbleHeight: halfBubbleHeight,
            arrowHorizontalThreshold: arrowHorizontalThreshold,
            arrowVerticalThreshold: arrowVerticalThreshold
          }).mount(document.createElement('div'));
          // 绑定SELECT_EVENT到bubbleVM
          emitter.on(SELECT_EVENT, function (item) {
            emit(SELECT_EVENT, item);
          });
        }
        bubbleVMJustCreated = true;
      } else {
        bubbleVMJustCreated = false;
      }
    }
    function placeBubble() {
      if (!(bubbleVM && slotContainer && bubbleContainer)) {
        return;
      }
      var bubbleElRect = bubbleVM.$el.getBoundingClientRect();
      var slotElRect = slotContainer.getBoundingClientRect();
      var bubbleVMPosition = getRelativePosition(slotContainer, bubbleContainer); // 初始值为slot在bubbleContainer中的相对位置
      var bubbleVMPositionTemp = _objectSpread2({}, bubbleVMPosition);
      var adaptiveRemain = _toConsumableArray(popoverPositions); // 自适应列表
      var adaptiveRemainChanged = false; // 自适应列表是否改变，如果没有改变，说明位置已适应，可以渲染
      halfBubbleWidth.value = bubbleElRect.width / 2; // 把渲染之后bubble真实的尺寸数据传给bubble自身
      halfBubbleHeight.value = bubbleElRect.height / 2; // 把渲染之后bubble真实的尺寸数据传给bubble自身
      function computePosition() {
        bubbleVMPositionTemp = computeBubblePosition({
          bubbleVMPositionTemp: bubbleVMPositionTemp,
          position: position.value,
          positionOffset: props.bubbleJustifyOffset,
          slotElRect: slotElRect,
          bubbleElRect: bubbleElRect,
          arrowHorizontalThreshold: arrowHorizontalThreshold.value,
          arrowVerticalThreshold: arrowVerticalThreshold.value
        });
      }
      computePosition();
      if (props.isAdaptive) {
        var adaptiveContainer = getFirstInvisibleWrapper(slotContainer);
        var adaptiveContainerOffsetTop = adaptiveContainer.offsetTop;
        var adaptiveContainerOffsetLeft = adaptiveContainer.offsetLeft;
        var bubbleOffsetParent = bubbleVM.$el.offsetParent;
        var bubbleOffsetParentOffsetTop = bubbleOffsetParent ? bubbleOffsetParent.offsetTop : 0;
        var bubbleOffsetParentOffsetLeft = bubbleOffsetParent ? bubbleOffsetParent.offsetLeft : 0;
        do {
          var adaptiveContainerRect = {
            top: adaptiveContainerOffsetTop,
            bottom: adaptiveContainerOffsetTop + adaptiveContainer.offsetHeight,
            left: adaptiveContainerOffsetLeft,
            right: adaptiveContainerOffsetLeft + adaptiveContainer.offsetWidth
          };
          var bubbleRect = {
            top: bubbleOffsetParentOffsetTop + bubbleVMPositionTemp.y,
            bottom: bubbleOffsetParentOffsetTop + bubbleVMPositionTemp.y + bubbleElRect.height,
            left: bubbleOffsetParentOffsetLeft + bubbleVMPositionTemp.x,
            right: bubbleOffsetParentOffsetLeft + bubbleVMPositionTemp.x + bubbleElRect.width
          };
          bubbleVMPositionTemp = _objectSpread2({}, bubbleVMPosition);
          var res = findBestBubblePosition({
            adaptiveContainerRect: adaptiveContainerRect,
            bubbleRect: bubbleRect,
            adaptiveRemain: adaptiveRemain,
            adaptingPosition: position.value,
            preferPosition: props.position
          }); // 更新箭头位置
          position.value = res.adaptedPosition;
          adaptiveRemainChanged = res.adaptiveRemainChanged;
          computePosition();
        } while (adaptiveRemainChanged && adaptiveRemain.length > 0);
      }
      bubbleVMPosition = bubbleVMPositionTemp;
      bubbleVM.$el.style.cssText = "position: absolute; top: ".concat(bubbleVMPosition.y, "px; left: ").concat(bubbleVMPosition.x, "px");
    }
    function hideBubble() {
      var ele = bubbleVM && bubbleVM.$el;
      if (ele && ele.parentNode !== null) {
        ele.classList.add('bubble-transition-leave-to');
        ele.classList.add('bubble-transition-leave-active');
        var transitionDuration = window.getComputedStyle(ele).transitionDuration;
        var duration;
        if (/s$/g.test(transitionDuration)) {
          duration = parseFloat(transitionDuration) * 1000;
        }
        setTimeout(function () {
          if (ele.parentNode) {
            ele.parentNode.removeChild(ele);
          }
          bubbleVM = null;
          emit(HIDE_EVENT);
          emit(INPUT_EVENT, false);
        }, duration);
      }
    }
    function handleSlotClick() {
      var ele = bubbleVM && bubbleVM.$el;
      if (ele && ele.parentNode !== null) {
        hideBubble();
      } else {
        throttle(showBubble, 250)();
      }
    }
    function handleScroll() {
      // 滚动时给placeBubble加上节流，1000 / 30 ≈ 30
      throttle(placeBubble, 30)();
    }
    watch(function () {
      return props;
    }, function (val) {
      if (val.modelValue) {
        showBubble();
      } else {
        hideBubble();
      }
    }, {
      deep: true
    });
    watch(function () {
      return props.position;
    }, function (newPosition) {
      position.value = newPosition;
    });
    // 挂载时，获取放置bubble的container和放置slot的container
    onMounted(function () {
      if (firstSlotNode) {
        slotContainer = firstSlotNode.el;
        bubbleContainer = getFirstScrollWrapper(slotContainer);
        timer = null;
        props.modelValue && showBubble();
      }
      // 如果开启了点击任意位置隐藏
      if (props.touchHide) {
        document.addEventListener('touchend', hideBubble);
        document.addEventListener('mouseup', hideBubble);
      }
    });
    onBeforeUnmount(function () {
      document.removeEventListener('touchend', hideBubble);
      document.removeEventListener('mouseup', hideBubble);
      bubbleContainer && bubbleContainer.removeEventListener('scroll', handleScroll);
    });
    onUnmounted(function () {
      hideBubble();
    });
    // 渲染时，把slot的firstNode渲染到页面上
    return function () {
      if (!slots["default"]) {
        return slots["default"];
      }
      var defaultSlot = slots["default"]();
      if (defaultSlot.length > 1) {
        console.warn('Popover: <sp-popover></sp-popover> 的默认插槽包裹多个子元素时，只显示第一个元素');
      }
      firstSlotNode = defaultSlot[0];
      if (firstSlotNode.type === Text) {
        console.warn('Popover: 请给插槽包裹一个标签，否则将不能计算布局和样式，导致报错');
      }
      if (props.touchShow) {
        // 取slot的第一个div，将showBubble绑定到他的click事件上去
        bindOurFnToEventOfNode(firstSlotNode, 'onClick', handleSlotClick);
      }
      return firstSlotNode;
    };
  }
});

var _Popover = Popover;

export { _Popover as default };
